/**
 * TopRecommendationsCard - Shows the top 5 most impactful recommendations
 * across all categories, sorted by priority then category score.
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Button,
  Tooltip,
} from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TopRecommendation } from '../types/api';
import { CATEGORY_CONFIG, V2CategoryKey, getScoreColor } from '../utils/scoringUtils';
import { safeText } from '../utils/safeText';

interface TopRecommendationsCardProps {
  recommendations: TopRecommendation[];
}

const getPriorityColor = (priority: number) => {
  if (priority <= 1) return 'error';
  if (priority <= 2) return 'warning';
  if (priority <= 3) return 'info';
  return 'success';
};

const getPriorityLabel = (priority: number): string => {
  if (priority <= 1) return 'Kritisch';
  if (priority <= 2) return 'Hoch';
  if (priority <= 3) return 'Mittel';
  return 'Niedrig';
};

const TopRecommendationsCard: React.FC<TopRecommendationsCardProps> = ({ recommendations }) => {
  const [showAll, setShowAll] = useState(false);

  if (!recommendations || recommendations.length === 0) return null;

  // Show max 5 initially
  const topRecs = recommendations.slice(0, 5);
  const extraRecs = recommendations.slice(5);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <StarIcon color="primary" />
          <Tooltip title="Die wichtigsten Handlungsempfehlungen zur Verbesserung Ihrer AI-Sichtbarkeit, priorisiert nach Dringlichkeit und Wirkung." arrow>
            <Typography variant="h6" sx={{ cursor: 'help' }}>
              Top-Empfehlungen
            </Typography>
          </Tooltip>
        </Box>

        <List dense>
          {topRecs.map((rec, idx) => {
            const catConfig = CATEGORY_CONFIG[rec.category_id as V2CategoryKey];
            return (
              <ListItem key={idx} sx={{ pl: 0, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                  <PriorityHighIcon
                    fontSize="small"
                    sx={{ color: getScoreColor(rec.priority <= 2 ? 30 : 70) }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                      <Typography variant="body2" fontWeight="medium">
                        {safeText(rec.title)}
                      </Typography>
                      <Chip
                        label={getPriorityLabel(rec.priority)}
                        size="small"
                        color={getPriorityColor(rec.priority) as any}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    catConfig && (
                      <Typography variant="caption" color="text.secondary">
                        {catConfig.name}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            );
          })}
        </List>

        {extraRecs.length > 0 && (
          <>
            <Collapse in={showAll}>
              <List dense>
                {extraRecs.map((rec, idx) => {
                  const catConfig = CATEGORY_CONFIG[rec.category_id as V2CategoryKey];
                  return (
                    <ListItem key={idx} sx={{ pl: 0, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        <PriorityHighIcon
                          fontSize="small"
                          sx={{ color: getScoreColor(rec.priority <= 2 ? 30 : 70) }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                            <Typography variant="body2" fontWeight="medium">{rec.title}</Typography>
                            <Chip
                              label={getPriorityLabel(rec.priority)}
                              size="small"
                              color={getPriorityColor(rec.priority) as any}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        }
                        secondary={
                          catConfig && (
                            <Typography variant="caption" color="text.secondary">
                              {catConfig.name}
                            </Typography>
                          )
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
            <Button
              size="small"
              onClick={() => setShowAll(!showAll)}
              endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mt: 1 }}
            >
              {showAll ? 'Weniger anzeigen' : `Weitere ${extraRecs.length} Optimierungen`}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TopRecommendationsCard;
